import type { SessionPort } from "../../mock/session-port";
import { createSessionStore, type SessionStore } from "../session-store";
import type { IdentityApi } from "../types";
import { createActivityTracker } from "./activity";
import { createLiveIdentity } from "./index";
import { createLiveSessionPort } from "./session-port";
import { browserSignals } from "./signals";

/**
 * Everything VITE_IDENTITY_MODE=live needs, built in one call for main.tsx: the live IdentityApi, the tab's
 * session store on top of it, and the session port the projects mock reads until the projects service exists.
 * There is no staff console or demo tooling in live mode (`IdentityServices.staff` and `.demo` stay undefined).
 */
export interface LiveServicesOptions {
  /** VITE_IDENTITY_API_PATH. */
  apiPath: string;
  /** How often an open tab re-checks its session (VITE_SESSION_CHECK_SECONDS), in ms. */
  checkIntervalMs: number;
  now: () => number;
  /** Project number prefix for the projects mock (VITE_DEV_ORG_PROJECT_PREFIX). */
  projectPrefix: string;
}

export interface LiveServices {
  identity: IdentityApi;
  session: SessionStore;
  port: SessionPort;
}

export function createLiveServices({
  apiPath,
  checkIntervalMs,
  now,
  projectPrefix,
}: LiveServicesOptions): LiveServices {
  const identity = createLiveIdentity({
    apiPath,
    // A plain call keeps fetch bound to the window.
    fetch: (input, init) => fetch(input, init),
    signals: browserSignals(),
    activity: createActivityTracker(typeof window === "undefined" ? undefined : window),
  });
  const session = createSessionStore(identity, { checkIntervalMs, now });
  return { identity, session, port: createLiveSessionPort(session, { projectPrefix }) };
}
